import { Map, OrderedSet } from 'immutable';

import { Actions } from '../../../actions/index';
import { CellRunActions } from '../../../actions/notebooks/cell-run.actions';
import { CellActions } from '../../../actions/notebooks/cell.actions';
import { NotebookActions } from '../../../actions/notebooks/notebook.actions';
import { NotebookCellRunResultResource } from '@mode/legacy/data-access-model-js';
import { NormalizedNotebookCellRunResultArray } from '@mode/legacy/types-app-state';

export const initialCellRunResultsState: NormalizedNotebookCellRunResultArray = {
  tokens: OrderedSet<string>(),
  resultTypes: Map<string, string>(),
  dates: Map<string, string>(),
  contentTypes: Map<string, string>(),
  contents: Map<string, string>(),
  resources: Map<string, NotebookCellRunResultResource>(),
};

export function tokensReducer(
  state = initialCellRunResultsState.tokens,
  action: Actions
): NormalizedNotebookCellRunResultArray['tokens'] {
  switch (action.type) {
    case CellRunActions.Type.CreateRunSuccess: // falls through
    case CellRunActions.Type.LoadLastCellRunSuccess: // falls through
    case CellActions.Type.DestroyCellSuccess: {
      if (action.runToken && action.resultTokens) {
        return state.filterNot((token) => action.resultTokens.contains(token));
      }
      break;
    }
    case CellRunActions.Type.LoadCellRunResultsSuccess:
      return state.merge(action.resultResources.map((res) => res.token));
    case CellRunActions.Type.AddCellToken:
      return state.add(action.token);
    case CellRunActions.Type.LoadLayoutCellRunSuccess: {
      if (action.oldRunToken && action.oldResultTokens) {
        return state.filterNot((token) => action.oldResultTokens.contains(token));
      }
      break;
    }
    case NotebookActions.Type.CreateRunSuccess: {
      return state.filterNot((_, resultToken) => action.resultTokens.has(resultToken));
    }
  }

  return state;
}

export function resultTypesReducer(
  state = initialCellRunResultsState.resultTypes,
  action: Actions
): NormalizedNotebookCellRunResultArray['resultTypes'] {
  switch (action.type) {
    case CellRunActions.Type.CreateRunSuccess: // falls through
    case CellRunActions.Type.LoadLastCellRunSuccess: // falls through
    case CellActions.Type.DestroyCellSuccess: {
      if (action.runToken && action.resultTokens) {
        return state.removeAll(action.resultTokens);
      }
      break;
    }
    case CellRunActions.Type.LoadCellRunResultsSuccess:
      return state.merge(action.resultResources.map<[string, string]>((res) => [res.token, res.result_type]));
    case CellRunActions.Type.LoadCellRunResultSuccess:
      return state.set(action.resultResource.token, action.resultResource.result_type);
    case CellRunActions.Type.LoadLayoutCellRunSuccess: {
      if (action.oldRunToken && action.oldResultTokens) {
        return state.removeAll(action.oldResultTokens);
      }
      break;
    }
    case NotebookActions.Type.CreateRunSuccess: {
      return state.filterNot((_, resultToken) => action.resultTokens.has(resultToken));
    }
  }

  return state;
}

export function datesReducer(
  state = initialCellRunResultsState.dates,
  action: Actions
): NormalizedNotebookCellRunResultArray['dates'] {
  switch (action.type) {
    case CellRunActions.Type.CreateRunSuccess: // falls through
    case CellRunActions.Type.LoadLastCellRunSuccess: // falls through
    case CellActions.Type.DestroyCellSuccess: {
      if (action.runToken && action.resultTokens) {
        return state.removeAll(action.resultTokens);
      }
      break;
    }

    case CellRunActions.Type.LoadCellRunResultsSuccess:
      return state.merge(action.resultResources.map<[string, string]>((res) => [res.token, res.date]));

    case CellRunActions.Type.LoadCellRunResultSuccess:
      return state.set(action.resultResource.token, action.resultResource.date);

    case CellRunActions.Type.LoadLayoutCellRunSuccess: {
      if (action.oldRunToken && action.oldResultTokens) {
        return state.removeAll(action.oldResultTokens);
      }
      break;
    }

    case NotebookActions.Type.CreateRunSuccess: {
      return state.filterNot((_, resultToken) => action.resultTokens.has(resultToken));
    }
  }

  return state;
}

export function contentTypesReducer(
  state = initialCellRunResultsState.contentTypes,
  action: Actions
): NormalizedNotebookCellRunResultArray['contentTypes'] {
  switch (action.type) {
    case CellRunActions.Type.CreateRunSuccess: // falls through
    case CellRunActions.Type.LoadLastCellRunSuccess: // falls through
    case CellActions.Type.DestroyCellSuccess: {
      if (action.runToken && action.resultTokens) {
        return state.removeAll(action.resultTokens);
      }
      break;
    }
    case CellRunActions.Type.LoadCellRunResultsSuccess:
      return state.merge(action.resultResources.map<[string, string]>((res) => [res.token, res.content_type]));
    case CellRunActions.Type.LoadCellRunResultSuccess:
      return state.set(action.resultResource.token, action.resultResource.content_type);
    case CellRunActions.Type.LoadLayoutCellRunSuccess: {
      if (action.oldRunToken && action.oldResultTokens) {
        return state.removeAll(action.oldResultTokens);
      }
      break;
    }
    case NotebookActions.Type.CreateRunSuccess:
      return state.filterNot((_, resultToken) => action.resultTokens.has(resultToken));
  }

  return state;
}

export function contentsReducer(
  state = initialCellRunResultsState.contents,
  action: Actions
): NormalizedNotebookCellRunResultArray['contents'] {
  switch (action.type) {
    case CellRunActions.Type.CreateRunSuccess: // falls through
    case CellRunActions.Type.LoadLastCellRunSuccess: // falls through
    case CellActions.Type.DestroyCellSuccess: {
      if (action.runToken && action.resultTokens) {
        return state.removeAll(action.resultTokens);
      }
      break;
    }
    case CellRunActions.Type.LoadCellRunResultContentSuccess:
      return state.set(action.resultToken, action.content);
    case CellRunActions.Type.LoadLayoutCellRunSuccess: {
      if (action.oldRunToken && action.oldResultTokens) {
        return state.removeAll(action.oldResultTokens);
      }
      break;
    }
    case NotebookActions.Type.CreateRunSuccess:
      return state.filterNot((_, resultToken) => action.resultTokens.has(resultToken));
    case CellActions.Type.ClearCellOutput:
      return state.set(action.cellToken, null);
  }

  return state;
}

export function resourcesReducer(
  state = initialCellRunResultsState.resources,
  action: Actions
): NormalizedNotebookCellRunResultArray['resources'] {
  switch (action.type) {
    case CellRunActions.Type.CreateRunSuccess: // falls through
    case CellRunActions.Type.LoadLastCellRunSuccess: // falls through
    case CellActions.Type.DestroyCellSuccess: {
      if (action.runToken && action.resultTokens) {
        return state.removeAll(action.resultTokens);
      }
      break;
    }
    case CellRunActions.Type.LoadCellRunResultsSuccess:
      return state.merge(
        action.resultResources.map<[string, NotebookCellRunResultResource]>((res) => [res.token, res])
      );
    case CellRunActions.Type.LoadCellRunResultSuccess:
      return state.set(action.resultResource.token, action.resultResource);
    case CellRunActions.Type.LoadLayoutCellRunSuccess: {
      if (action.oldRunToken && action.oldResultTokens) {
        return state.removeAll(action.oldResultTokens);
      }
      break;
    }
    case NotebookActions.Type.CreateRunSuccess:
      return state.filterNot((_, resultToken) => action.resultTokens.has(resultToken));
  }

  return state;
}

export const cellRunResultsReducer = {
  tokens: tokensReducer,
  resultTypes: resultTypesReducer,
  dates: datesReducer,
  contentTypes: contentTypesReducer,
  contents: contentsReducer,
  resources: resourcesReducer,
};
